import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ColorConfig, UserDetails } from "../models/UserDetails";
import { SportDetails } from "../models/SportsDetails";
import { SiteConfig } from "../models/SiteConfig";
import type { RootState } from './store';

export interface DynamicTextColors {
  pageTextColorClass: string;
  inputBoxTextColorClass: string;
  buttonTextColorClass: string;
  accordionHeaderTextColorClass: string;
  tabTextColorClass: string;
  activeTabTextColorClass: string;
  tableHeaderTextColorClass: string;
  tableRowTextColorClass: string;
  tableTextColorClass: string;
  sbMarketNameTextColorClass: string;
  tabButtonTextColorClass: string;
  accordionSummaryTextColorClass: string;
  teamInfoTextColorClass: string;
  ionButtonTextColorClass: string;
  backgroundCardTextColorClass: string;
  headerTextColorClass: string;
}

export interface UserDetailsDTO {
  userDetails: UserDetails;
  siteConfig: SiteConfig;
  wallets: any;
  sportsExchData: any;
  sportSBData: any;
  casinoData: any;
  homeData: any;
  banners: any;
  activeWallet: any;
  Thumbnail: any;
  lobby: string;
  exchData: SportDetails;
  sbData: SportDetails;
  logout: boolean;
  isLoggedIn: boolean;
  Browser: string;
  colorConfig: ColorConfig;
  dynamicTextColors: DynamicTextColors | null;
  /**
   * Non-null only when `/api/v1/auth/group-config` returned `status !== "RS_OK"`
   * and the backend provided a channel message (`telegramMessage` or `webMessage`).
   * When set, the app must not call further startup/auth APIs.
   */
  groupConfigFatalMessage: string | null;
}

// Define the initial state
const initialState: UserDetailsDTO = {
  userDetails: {
    EmailId: "",
    IsAffiliator: false,
    MobileNumber: "",
    ReferralCode: "",
    ReferralCount: 0,
    ReferrerId: "",
    Status: "",
    UserKey: "",
    UserId: "",
    UserName: "",
    ActiveWallet: "",
    Theme: "",
    Thumbnail: "",
    Wager: 0,
    WithdrawAmount: 0,
    IsClassic: false,
    language: "English",
    preferredLanguage: undefined,
    IsPremium: false,
    telegramLink: "",
    telegramWebAccountLinkUrl: "",
    BlockUserPayments: false,
    BlockBonus: false,
    IsContactOptional: false,
    BlockReferrals: false,
    disableDefaultTheme:false,
    exchangeProvider: "",
    sportsbookProvider: "",
    botId: "",
    userOrigin: "",
    allowWhitelabel: "",
  },
  siteConfig: {
    isPrivate: false,
    isContactOptional: false,
    blockReferrals: false,
    blockBonus: false,
    blockUserPayments: false,
    isSportsEnabled: true,
    isCasinoEnabled: true,
    isSitesEnabled: true,
    disableDefaultTheme: false,
    isIplThemeEnabled: false,
    apkUrl: "",
    demoLogin: false,
    languageConfig: undefined,
    contactInfo: {
      Facebook: "",
      Instagram: "",
      TelegramChannel: "",
      WhatsappSignup: "",
      WhatsappSupport: "",
      EnableCustomerSupport: false,
    },
    colorConfig: {
      activeButton: "",
      background: "",
      backgroundTeamInfo: "",
      filterColor: "",
      inputBackgroundBox:"",
      inputBorderBox:"",
      primary:"",
      primaryTeamInfo:"",
      subHeaderColor: "",
      tableBackground:"",
      tableBorder: "",
      tableHeader:"",
      text:"",
    },
    isCashoutEnabled: false,
    isTurboCashoutEnabled: false,
  },
  wallets: [],
  sportsExchData: [],
  sportSBData: [],
  casinoData: [],
  homeData: [],
  banners: [],
  activeWallet: "",
  Thumbnail: "",
  lobby: "EXCHANGE",
  exchData: {
    providerId: "",
    token: "",
    operatorId: "",
    partnerId: "",
    currency: "",
    rate: 0,
  },
  sbData: {
    providerId: "",
    token: "",
    operatorId: "",
    partnerId: "",
    currency: "",
    rate: 0,
  },
  logout: false,
  isLoggedIn: false,
  Browser: "",
  colorConfig:{
    activeButton:"",
    background:"",
    backgroundTeamInfo:"",
    filterColor:"",
    inputBackgroundBox:"",
    inputBorderBox:"",
    primary:"",
    primaryTeamInfo:"",
    subHeaderColor:"",
    tableBackground:"",
    tableBorder:"",
    tableHeader:"",
    text:"",
  },
  dynamicTextColors: null,
  groupConfigFatalMessage: null,
};

// Create a slice of the state with actions and reducers
const authSlice = createSlice({
  name: "userDetails",
  initialState,
  reducers: {
    setUserDetails(state, action: PayloadAction<Partial<UserDetails>>) {
      state.userDetails = { ...state.userDetails, ...action.payload };
    },
    setSiteConfig(state, action: PayloadAction<Partial<SiteConfig>>) {
      state.siteConfig = { 
        ...state.siteConfig, 
        ...action.payload,
        colorConfig: action.payload.colorConfig || state.siteConfig.colorConfig
      };
    },
    setWallets(state, action: PayloadAction<any[]>) {
      state.wallets = action.payload;
    },
    setSportsExchData(state, action: PayloadAction<any[]>) {
      state.sportsExchData = action.payload;
    },
    setSportsSBData(state, action: PayloadAction<any[]>) {
      state.sportSBData = action.payload;
    },
    setCasinoData(state, action: PayloadAction<any[]>) {
      state.casinoData = action.payload;
    },
    setHomeData(state, action: PayloadAction<any[]>) {
      state.homeData = action.payload;
    },
    setBanners(state, action: PayloadAction<any[]>) {
      state.banners = action.payload;
    },
    setActiveWallet(state, action: PayloadAction<any>) {
      state.activeWallet = action.payload;
    },
    setUserThumbnail(stage, action: PayloadAction<any>) {
      stage.Thumbnail = action.payload;
    },
    setLobby(stage, action: PayloadAction<any>) {
      stage.lobby = action.payload;
    },
    setUserName(state, action: PayloadAction<any>) {
      state.userDetails.UserName = action.payload;
    },
    setExchangeAuthData(state, action: PayloadAction<any>) {
      state.exchData = action.payload;
    },
    setSportBookAuthData(state, action: PayloadAction<any>) {
      state.sbData = action.payload;
    },
    setLogout(state, action: PayloadAction<any>) {
      state.logout = action.payload;
    },
    setSportView(state, action: PayloadAction<any>) {
      state.userDetails.IsClassic = action.payload;
    },
    setIsLoggedIn(state, action: PayloadAction<any>) {
      state.isLoggedIn = action.payload
    },
    setBrowser(state, action: PayloadAction<any>) {
      state.Browser = action.payload
    },
    setColorConfig(state, action: PayloadAction<Partial<ColorConfig>>) {
      state.colorConfig = { ...state.colorConfig, ...action.payload };
    },

    setDynamicTextColors(state, action: PayloadAction<DynamicTextColors>) {
      state.dynamicTextColors = action.payload;
    },
    setGroupConfigFatalMessage(state, action: PayloadAction<string | null>) {
      state.groupConfigFatalMessage = action.payload;
    },
    // Define other reducers here if needed
  },
});

// Export the actions generated by createSlice
export const { setUserDetails, setSiteConfig, setWallets, setSportsExchData, setSportsSBData, setCasinoData, setHomeData, setBanners, setActiveWallet, setUserThumbnail, setUserName, setExchangeAuthData, setSportBookAuthData, setLobby, setLogout, setSportView, setIsLoggedIn, setBrowser, setColorConfig, setDynamicTextColors, setGroupConfigFatalMessage} = authSlice.actions;

// Export the reducer to be used in the store
export default authSlice.reducer;
